import { useMemo, createContext, useContext } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
export const ElementsContext = /*#__PURE__*/createContext(null);
/**
 * Provider that stores the override component and props for an element.
 *
 * @public
 */
export function ElementsProvider(_ref) {
  let {
    children,
    defaultProps,
    kind
  } = _ref;
  const ctx = useElements();
  const value = useMemo(() => {
    // If kind is specified, we assume we are in nested element so we get the parent registry
    // Ideally we'd use WeakMap but node < 20 does not support symbols as keys
    const registry = kind && (ctx === null || ctx === void 0 ? void 0 : ctx.registry) || new Map();
    const registeredOverrideComponents = kind && (ctx === null || ctx === void 0 ? void 0 : ctx.registeredOverrideComponents) || new Map();
    return {
      registry,
      registeredOverrideComponents,
      register: (kind, value) => {
        if (!registeredOverrideComponents.get(kind)) {
          registeredOverrideComponents.set(kind, []);
        }
        const overrideComps = registeredOverrideComponents.get(kind) || [];
        if (!overrideComps.includes(value.Component)) {
          overrideComps.push(value.Component);
        }
        if (overrideComps[overrideComps.length - 1] === value.Component) {
          registry.set(kind, value);
        }
      },
      retrieve: kind => registry.get(kind),
      isNested: k => k === kind || Boolean(ctx === null || ctx === void 0 ? void 0 : ctx.isNested(k)),
      getProps: k => k === kind ? defaultProps : ctx === null || ctx === void 0 ? void 0 : ctx.getProps(k),
      getOuterProps: k => {
        var _registry$get;
        return Boolean(ctx === null || ctx === void 0 ? void 0 : ctx.isNested(k)) ? (_registry$get = registry.get(k)) === null || _registry$get === void 0 ? void 0 : _registry$get.outerProps : null;
      }
    };
  }, [kind, defaultProps]);
  return /*#__PURE__*/_jsx(ElementsContext.Provider, {
    value: value,
    children: children
  });
}
export const useElements = () => useContext(ElementsContext);

/**
 * Hook is used to retrieve the internal default props for an element.
 *
 * See documentation for more information.
 *
 * @public
 */
export function useElementProps(element, _temp) {
  var _process, _process$env, _useElements, _useElements2, _useElements3;
  let {
    mergeWith
  } = _temp === void 0 ? {} : _temp;
  if (typeof element.kind !== 'symbol') throw new Error('Invalid element argument. It must be an element or an element Component.');
  if (((_process = process) === null || _process === void 0 ? void 0 : (_process$env = _process.env) === null || _process$env === void 0 ? void 0 : _process$env.NODE_ENV) === 'development' && !((_useElements = useElements()) !== null && _useElements !== void 0 && _useElements.isNested(element.kind))) {
    throw new Error('Invalid element argument. Only props of parent elements can be retrieved.');
  }
  return {
    ...((_useElements2 = useElements()) === null || _useElements2 === void 0 ? void 0 : _useElements2.getProps(element.kind)),
    ...((_useElements3 = useElements()) === null || _useElements3 === void 0 ? void 0 : _useElements3.getOuterProps(element.kind)),
    ...mergeWith
  };
}